/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { IResourceInfo, IView, IViewInfo, IViewResource } from "../../../view/IView";
import { LayoutState } from "../../../ui/components/Layout/LayoutState";
import { AssetEditLayoutConfig } from "./Layout";
import { ResourceService } from "../../../resources/ResourceService";
import { IEditorResource_Asset, IEditorResource_Base } from "../../../resources/ResourceTypes";
import { IEditor } from "../../../IEditor";
import {
	createEventEmitter,
	emitEvent,
	onEvent,
	TGetSimpleEventEmitterDataType,
	TSimpleEventListener
} from "@adapptio/lib-shared";
import { ViewService } from "../../../view/ViewService";
import { IEditorApiService } from "../../../api/IEditorApiService";
import { RESOURCE_TYPES } from "@adapptio/lib-core";
import { isImageAsset, isVideoAsset } from "../utils";
import { AppConfigService } from "../../../config/AppConfigService";

export interface AssetEditParams {
	resourceId: string;
}

export enum ASSET_TYPE {
	IMAGE = "image",
	VIDEO = "video",
	FILE = "file"
}

export const assetIcons = {
	[ASSET_TYPE.IMAGE]: "mdi/file-image",
	[ASSET_TYPE.VIDEO]: "mdi/file-video",
	[ASSET_TYPE.FILE]: "mdi/file"
};

export class AssetEdit implements IView<AssetEditParams>, IViewResource {
	private layout = new LayoutState(AssetEditLayoutConfig);

	private apiSvc: IEditorApiService;
	private appConfigSvc: AppConfigService;
	private resourceSvc: ResourceService;
	private resource: IEditorResource_Asset;
	private viewId: string;
	private viewSvc: ViewService;

	public onViewInfoChanged = createEventEmitter();
	public onResourceInfoChanged = createEventEmitter();

	constructor(private dc: IEditor) {
		this.apiSvc = dc.get("apiService");
		this.appConfigSvc = dc.get("appConfigService");
		this.resourceSvc = dc.get("resourceService");
		this.viewSvc = dc.get("viewService");
	}

	public async init(params: AssetEditParams, viewId: string): Promise<void> {
		this.viewId = viewId;
		this.resource = (await this.resourceSvc.getBlueprintById(params.resourceId)) as IEditorResource_Asset;

		emitEvent(this.onViewInfoChanged);

		if (!this.resource) {
			throw new Error(`Resource '${params.resourceId}' was not found.`);
		}

		if (this.resource.resourceType !== RESOURCE_TYPES.ASSET) {
			throw new Error(`Resource '${params.resourceId}' is not an asset.`);
		}

		onEvent(this.resource.onChange, this.handleResourceChange);

		onEvent(this.resource.onDelete, this.handleResourceDelete);
	}

	private emitViewInfoChange(): void {
		emitEvent(this.onViewInfoChanged);
	}

	private emitResourceInfoChange(): void {
		emitEvent(this.onResourceInfoChanged);
	}

	private handleResourceChange = () => {
		this.emitViewInfoChange();
		this.emitResourceInfoChange();
	};

	private handleResourceDelete: TSimpleEventListener<
		TGetSimpleEventEmitterDataType<IEditorResource_Base["onDelete"]>
	> = () => {
		this.viewSvc.close(this.viewId);
	};

	public getLayout(): LayoutState {
		return this.layout;
	}

	public getResourceInfo(): IResourceInfo {
		return {
			resource: this.resource,
			parentList: this.resourceSvc.getResourceParentList(this.resource.id),
			isBeingSaved: false
		};
	}

	public getAssetType(): ASSET_TYPE {
		if (isImageAsset(this.resource)) {
			return ASSET_TYPE.IMAGE;
		}

		if (isVideoAsset(this.resource)) {
			return ASSET_TYPE.VIDEO;
		}

		return ASSET_TYPE.FILE;
	}

	public getViewInfo(): IViewInfo {
		return {
			icon: assetIcons[this.getAssetType()],
			label: this.resource?.descriptor?.name || "...",
			canClose: true,
			isModified: false,
			renderWhenNotActive: true
		};
	}

	public getAssetBaseUrl(): string {
		const appConfig = this.appConfigSvc.getConfig();
		const envs = appConfig?._app?.envs || { appUrlProtocol: "", appUrl: "" };

		const { apiUrlProtocol, apiUrl } = envs;

		if (apiUrlProtocol && apiUrl) {
			return `${apiUrlProtocol}://${apiUrl}`;
		}

		return this.apiSvc.getRealApiUrl();
	}

	public dispose() {
		this.layout.dispose();
	}
}
